/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import {toggleCollapseStyle} from '@ion/dls/src/components/navigation/useExpandCollapse';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  dlsSidebarDockedStyle,
  dlsSidebarOffset
} from '../components/navigation/NavigationStore';
import {useIsPhone} from '../hooks/useMedia';
import useRootManager from './useRootManager';

interface StyleProps {
  width?: number | string;
  height?: number | string;
  title?: string;
  framePadding?: number;
};

interface PersistentSDKIFrameProps {
  error?: boolean;
  loading?: boolean;
  styles?: GenericObject;
  fullScreen?: boolean;
  dashboardLayout?: boolean;
  iFrameId?: string;
  isNavCollapsed?: boolean;
  persistIFrame?: boolean;
  framePadding?: number;
  showFrame?: boolean;
};

/* 
 * Default name assigned to all containers of persistent iframes,
 * allowing easy targeting and removal from the DOM when needed.
 */
export const IFRAME_CONTAINER_NAME = 'sdkIFrameContainer';

// Default ID for persistent iframe
const DEFAULT_IFRAME_ID = 'sdkIFrameId';

const useStyles = makeStyles<DLSTheme>((theme) => ({
  frameContainer : {
    width        : toggleCollapseStyle('calc(100vw - 170px)', 'calc(100vw - 220px)'),
    height       : 'calc(-228px + 100vh)',
    left         : toggleCollapseStyle('123px', '180px'),
    position     : 'absolute',
    top          : '199px',
    paddingTop   : ({framePadding}: StyleProps) => framePadding,
    '& > iframe' : {
      height : ({title}: StyleProps) => !title ? '100%' : '92%',
      width  : '98%',
      border : 'none'
    },
    [theme.breakpoints.down('md')] : {
      width  : toggleCollapseStyle('calc(100vw - 170px)', 'calc(100vw - 220px)'),
      height : 'calc(80vh - 70px)'
    }
  },
  currentFrameContainer : {
    ...dlsSidebarDockedStyle,
    height : 'calc(100vh - 164px) !important',
    top    : '148px !important'
  },
  fullScreen : {
    width        : toggleCollapseStyle('calc(100vw - 50px)', 'calc(100vw - 105px)'),
    height       : 'calc(100vh - 52px)',
    left         : ({collapsed}: {collapsed: boolean}) => dlsSidebarOffset(collapsed),
    position     : 'absolute',
    top          : '45px',
    '& > iframe' : {
      height : ({title}: StyleProps) => !title ? '100%' : '92%',
      width  : '98%',
      border : 'none'
    },
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 103px)',
      width  : '100% !important',
      top    : '95px',
      left   : '0px !important'
    }
  },
  currentFullScreen : {
    ...dlsSidebarDockedStyle
  },
  dashboard : {
    width        : '100%',
    height       : 'calc(100vh - 320px)',
    left         : 0,
    position     : 'absolute',
    top          : toggleThemeStyle('264px', 'inherit'),
    '& > iframe' : {
      height : ({title}: StyleProps) => !title ? '100%' : '92%',
      width  : '98%',
      border : 'none'
    },
    [theme.breakpoints.down('sm')] : {
      width  : '100% !important',
      height : '-webkit-fill-available'
    }
  },
  customStyles : {
    zIndex       : 99,
    '& > iframe' : {
      border : 'none',
      height : '100%'
    }
  }
}));

/**
 * The usePersistentSDKIFrame hook provides a utility to store an iFrame in the DOM,
 * allowing it to be reused without needing to reload.
 * 
 * ```typescript
 * import usePersistentSDKIFrame from '@ion/dls/src/client/usePersistentSDKIFrame';
 * 
 * usePersistentSDKIFrame(params);
 * 
 * ```
 */
export const usePersistentSDKIFrame = ({
  error,
  loading,
  styles,
  showFrame = true,
  framePadding = 0,
  fullScreen = false,
  dashboardLayout = false,
  persistIFrame = false,
  isNavCollapsed = false,
  iFrameId = DEFAULT_IFRAME_ID
}: PersistentSDKIFrameProps) => {
  const classes     = useStyles({collapsed: isNavCollapsed, framePadding});
  const isPhone     = useIsPhone();
  const iFrameIdRef = useRef(iFrameId);

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const {
    getContainer,
    removeContainer
  } = useRootManager(false);

  const iFrameContainer = persistIFrame ? getContainer(iFrameId) : null;

  const removePersistentSDKIFrames = useCallback(() => {
    document.querySelectorAll(`div[name="${IFRAME_CONTAINER_NAME}"]`)
      ?.forEach(iframe => removeContainer(iframe));
  }, []);

  const hideIFrame = useCallback(id => {
    const iFrameContainer = getContainer(id);
    if (iFrameContainer && persistIFrame) {
      iFrameContainer.setAttribute('name', IFRAME_CONTAINER_NAME);
      iFrameContainer.style.display = 'none';
      iFrameContainer.style.pointerEvents = 'none';
    }
  }, [persistIFrame]);

  useEffect(() => {
    if (iFrameIdRef.current) {
      hideIFrame(iFrameIdRef.current);
    }
    iFrameIdRef.current = iFrameId;
  }, [iFrameId]);

  const setFrameStyles = useCallback(() => {
    const iFrameContainer = getContainer(iFrameIdRef.current);
    if (iFrameContainer && showFrame && styles) {
      const {width, height, top, left} = styles;
      iFrameContainer.style.display  = 'block';
      iFrameContainer.style.pointerEvents = 'auto';
      iFrameContainer.style.position = 'absolute';
      iFrameContainer.style.top      = `${top}px`;
      iFrameContainer.style.left     = `${left + 4}px`;
      iFrameContainer.style.height   = `${height - 8}px`;
      iFrameContainer.style.width    = `${width - 12}px`;
      iFrameContainer.className      = classes.customStyles;
      iFrameContainer.setAttribute('name', IFRAME_CONTAINER_NAME);
    } else if (iFrameContainer && !showFrame) {
      iFrameContainer.setAttribute('name', IFRAME_CONTAINER_NAME);
      iFrameContainer.style.display = 'none';
      iFrameContainer.style.pointerEvents = 'none';
    }
  }, [styles, showFrame]);

  useEffect(() => {
    if (styles) {
      setFrameStyles();
    }
  }, [styles, showFrame]);

  useEffect(() => {
    if (persistIFrame && iFrameContainer && !loading && !error) {
      iFrameContainer.setAttribute('name', IFRAME_CONTAINER_NAME);
      iFrameContainer.style.display = 'block';
      iFrameContainer.style.pointerEvents = 'auto';
      if (!styles) {
        iFrameContainer.className = dashboardLayout && isPhone ? classes.dashboard
          : (!fullScreen ? classnames(classes.frameContainer, {
            [classes.currentFrameContainer] : dashboardLayout && isCurrentTheme && !isPhone
          }) : classnames(classes.fullScreen, {
            [classes.currentFullScreen] : isCurrentTheme && !isPhone
          }));
      } else {
        setFrameStyles();
      }
    };
  }, [persistIFrame, iFrameId, loading, error, fullScreen, dashboardLayout, isPhone, isCurrentTheme]);

  useEffect(() => {
    return () => {
      hideIFrame(iFrameIdRef.current);
    };
  }, [persistIFrame]);

  return {removePersistentSDKIFrames};
};

export default usePersistentSDKIFrame;
